// THE TOPOLOGY's painter (#6778, prototype `topo.draw`, `:4011-4063`), cut by layer: the
// grid and links, the intake node, the machine hexagons, the GitHub and production nodes,
// and the travelling dots. The node NUMBERS are counts #6777 serves, never a count of the
// bounded item list: intake `counts.queued`, GitHub `counts.in_ci`, production
// `shipped_today.count`.
//
// A link is lit while a dot sits on it (the prototype's `heat`, read off the real places
// instead of a simulated travel). `motion: false` (reduced motion) is a STILL FRAME: dots
// snap, the intake ring and the CI arc stand still, and no trail, flash or spark is drawn.

import { t } from '../i18n';
import type { FleetLiveModel, LiveItem, LiveMachine } from './fleet-live';
import {
  alpha,
  cut,
  hexPath,
  MONO,
  type Paint2D,
  paintSparks,
  roundRect,
} from './fleet-live-canvas';
import { cpuColor } from './fleet-live-deck-paint';
import { burst, MOTION_MS, type Motion, type Particle, stepParticles } from './fleet-live-motion';
import { type LivePalette, toneColor } from './fleet-live-palette';
import {
  controls,
  type DotPlace,
  dotPlace,
  hexLines,
  type Pt,
  type TopoLayout,
} from './fleet-live-topology';

export type TopoFrame = {
  readonly l: TopoLayout;
  readonly p: LivePalette;
  readonly model: FleetLiveModel;
  readonly motion: Motion | null;
  readonly now: number;
  readonly dt: number;
};

function link(ctx: Paint2D, l: TopoLayout, a: Pt, b: Pt, col: string, w = 1): void {
  const [c0, c1] = controls(l, a, b);
  ctx.strokeStyle = col;
  ctx.lineWidth = w;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.bezierCurveTo(c0.x, c0.y, c1.x, c1.y, b.x, b.y);
  ctx.stroke();
  ctx.lineWidth = 1;
}

function paintLinks(ctx: Paint2D, f: TopoFrame, lit: ReadonlySet<string>): void {
  const { l, p } = f;
  const base = alpha(p.text, p.dark ? 0.06 : 0.08);
  l.ms.forEach((m, i) => {
    link(ctx, l, l.intake, m, base);
    if (lit.has(`in${i}`)) link(ctx, l, l.intake, m, alpha(p.live, 0.5), 1.5);
    link(ctx, l, m, l.gh, base);
    if (lit.has(`gh${i}`)) link(ctx, l, m, l.gh, alpha(p.live, 0.5), 1.5);
  });
  link(ctx, l, l.gh, l.prod, alpha(p.text, 0.12));
  if (lit.has('prod')) link(ctx, l, l.gh, l.prod, alpha(p.green, 0.6), 1.6);
}

function caption(ctx: Paint2D, p: LivePalette, text: string, x: number, y: number): void {
  ctx.font = `500 9.5px ${MONO}`;
  ctx.fillStyle = p.t3;
  ctx.fillText(text.toUpperCase(), x, y);
}

function number(
  ctx: Paint2D,
  p: LivePalette,
  n: number,
  x: number,
  y: number,
  small = false,
): void {
  ctx.font = `600 ${small ? 10 : n > 9999 ? 10 : 13}px ${MONO}`;
  ctx.fillStyle = p.text;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(n), x, y - 1);
}

function paintIntake(ctx: Paint2D, f: TopoFrame): void {
  const { l, p, model } = f;
  const rot = f.motion === null ? 0 : f.now / 1800;
  ctx.strokeStyle = p.line;
  ctx.beginPath();
  ctx.arc(l.intake.x, l.intake.y, 26, 0, 7);
  ctx.stroke();
  ctx.strokeStyle = alpha(p.live, 0.7);
  ctx.lineWidth = 1.5;
  for (let s = 0; s < 3; s += 1) {
    ctx.beginPath();
    ctx.arc(l.intake.x, l.intake.y, 30, rot + s * 2.09, rot + s * 2.09 + 1.1);
    ctx.stroke();
  }
  ctx.lineWidth = 1;
  number(ctx, p, model.topology.queued, l.intake.x, l.intake.y);
  if (l.v) {
    ctx.textAlign = 'left';
    caption(ctx, p, t('fleet.live.node_intake_queued'), l.intake.x + 40, l.intake.y);
    ctx.textAlign = 'center';
  } else {
    caption(ctx, p, t('fleet.live.node_intake'), l.intake.x, l.intake.y + 50);
    caption(ctx, p, t('fleet.live.node_queued'), l.intake.x, l.intake.y + 62);
  }
}

function paintHex(ctx: Paint2D, f: TopoFrame, m: LiveMachine, at: Pt): void {
  const { l, p, model } = f;
  hexPath(ctx, at.x, at.y, l.r);
  ctx.fillStyle = p.fill;
  ctx.fill();
  ctx.strokeStyle = m.busy ? alpha(p.live, 0.65) : p.line;
  ctx.lineWidth = m.busy ? 1.3 : 1;
  ctx.stroke();
  ctx.lineWidth = 2;
  // An unreported CPU draws no ring (V7).
  if (m.cpu !== null) {
    ctx.strokeStyle = alpha(p.text, 0.08);
    ctx.beginPath();
    ctx.arc(at.x, at.y, l.r + 7, 0, 7);
    ctx.stroke();
    // Neutral, amber at 70% or more, red above 90% (the prototype's normal band was the
    // accent, `:4027`; brief.md item 64).
    ctx.strokeStyle = cpuColor(p, m.cpu, alpha(p.text, 0.45));
    ctx.beginPath();
    const end = -Math.PI / 2 + (Math.PI * 2 * Math.min(100, m.cpu)) / 100;
    ctx.arc(at.x, at.y, l.r + 7, -Math.PI / 2, end);
    ctx.stroke();
  }
  ctx.lineWidth = 1;
  ctx.font = `500 9.5px ${MONO}`;
  ctx.fillStyle = m.busy ? p.text : p.t3;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(cut(m.name, l.v ? 9 : 13), at.x, at.y + l.r + 20);
  const lines = hexLines(model, m.id, t('fleet.live.idle').toUpperCase());
  ctx.font = `500 9px ${MONO}`;
  lines.forEach((line, j) => {
    // Coloured from the line's OWN item, never a fleet-wide set of tags (a tag is a label).
    const blocked = line.item?.state === 'blocked';
    ctx.fillStyle = blocked ? p.red : lines.length === 1 && m.jobs === 0 ? p.t3 : p.t2;
    ctx.fillText(cut(line.text, 7), at.x, at.y + (lines.length > 1 ? (j ? 6 : -6) : 0));
  });
}

function paintNodes(ctx: Paint2D, f: TopoFrame, flash: { gh: number; prod: number }): void {
  const { l, p, model } = f;
  roundRect(ctx, l.gh.x - 26, l.gh.y - 26, 52, 52, 10);
  ctx.fillStyle = p.fill;
  ctx.fill();
  ctx.strokeStyle = flash.gh > 0 ? alpha(p.red, 0.4 + 0.6 * flash.gh) : p.line;
  ctx.stroke();
  if (model.topology.inCi > 0 && f.motion !== null) {
    const a = f.now / 300;
    ctx.strokeStyle = alpha(p.live, 0.8);
    ctx.lineWidth = 1.5;
    for (const off of [0, Math.PI]) {
      ctx.beginPath();
      ctx.arc(l.gh.x, l.gh.y, 40, a + off, a + off + 1.4);
      ctx.stroke();
    }
    ctx.lineWidth = 1;
  }
  number(ctx, p, model.topology.inCi, l.gh.x, l.gh.y);
  caption(ctx, p, t('fleet.live.node_github'), l.gh.x, l.gh.y + 54);
  caption(
    ctx,
    p,
    t(l.v ? 'fleet.live.node_prs_ci_short' : 'fleet.live.node_prs_ci'),
    l.gh.x,
    l.gh.y + 66,
  );
  ctx.strokeStyle = alpha(p.green, 0.45 + 0.55 * flash.prod);
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.arc(l.prod.x, l.prod.y, 24, 0, 7);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.strokeStyle = alpha(p.green, 0.25);
  ctx.setLineDash([2, 4]);
  ctx.beginPath();
  ctx.arc(l.prod.x, l.prod.y, 32 + flash.prod * 10, 0, 7);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = alpha(p.green, 0.08 + 0.15 * flash.prod);
  ctx.beginPath();
  ctx.arc(l.prod.x, l.prod.y, 24, 0, 7);
  ctx.fill();
  number(ctx, p, model.topology.shipped, l.prod.x, l.prod.y, model.topology.shipped > 9999);
  caption(ctx, p, t('fleet.live.node_production'), l.prod.x, l.prod.y + 50);
  caption(ctx, p, t('fleet.live.node_shipped'), l.prod.x, l.prod.y + 62);
}

type Dot = { x: number; y: number; trail: Pt[] };

type DotPaint = { at: Dot; place: DotPlace; col: string; fade: number; tag: string };

function paintDot(ctx: Paint2D, f: TopoFrame, o: DotPaint): void {
  const { at, place, col, fade } = o;
  const { l, p } = f;
  if (at.trail.length > 2 && place.kind === 'link') {
    ctx.strokeStyle = alpha(col, 0.35 * fade);
    ctx.lineWidth = 2;
    ctx.beginPath();
    at.trail.forEach((q, j) => {
      if (j === 0) ctx.moveTo(q.x, q.y);
      else ctx.lineTo(q.x, q.y);
    });
    ctx.stroke();
    ctx.lineWidth = 1;
  }
  ctx.fillStyle = alpha(col, 0.22 * fade);
  ctx.beginPath();
  ctx.arc(at.x, at.y, 6, 0, 7);
  ctx.fill();
  ctx.fillStyle = alpha(col, fade);
  ctx.beginPath();
  ctx.arc(at.x, at.y, place.kind === 'queue' ? 2.2 : 3, 0, 7);
  ctx.fill();
  const clear =
    Math.hypot(at.x - l.intake.x, at.y - l.intake.y) > 50 &&
    Math.hypot(at.x - l.gh.x, at.y - l.gh.y) > 44;
  if (place.kind !== 'link' || !clear) return;
  ctx.font = `500 9px ${MONO}`;
  ctx.fillStyle = alpha(p.text, 0.8 * fade);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(cut(o.tag, 7), at.x + 8, at.y - 8);
}

export type TopoPainter = { readonly paint: (ctx: Paint2D, f: TopoFrame) => void };

type TopoState = {
  dots: Map<string, Dot>;
  sparks: Particle[];
  readonly seen: Set<number>;
  readonly flash: { gh: number; prod: number };
};

type Placed = { readonly item: LiveItem; readonly place: DotPlace };

/** The links carrying a dot this frame: lit, as the prototype's travel heat. */
function litLinks(places: readonly Placed[]): Set<string> {
  const lit = new Set<string>();
  for (const { item, place } of places) {
    if (place.link !== undefined) lit.add(place.link);
    if (place.kind === 'machine') lit.add(`in${item.machine}`);
  }
  return lit;
}

/** New ship and CI effects flash their node once; a ship also bursts at production. */
function noteEffects(f: TopoFrame, s: TopoState): void {
  const { l, motion, dt } = f;
  if (motion === null) {
    s.flash.gh = 0;
    s.flash.prod = 0;
    s.sparks = [];
    return;
  }
  const live = new Set(motion.effects.map((e) => e.seq));
  for (const seq of s.seen) if (!live.has(seq)) s.seen.delete(seq);
  for (const e of motion.effects) {
    if (s.seen.has(e.seq)) continue;
    s.seen.add(e.seq);
    if (e.kind === 'ship') {
      s.flash.prod = 1;
      s.sparks.push(...burst(l.prod.x, l.prod.y, 16));
    }
    if (e.kind === 'ci') s.flash.gh = 1;
  }
  s.flash.prod = Math.max(0, s.flash.prod - dt / MOTION_MS.flash);
  s.flash.gh = Math.max(0, s.flash.gh - dt / MOTION_MS.flash);
  s.sparks = stepParticles(s.sparks, dt);
}

function stepDot(f: TopoFrame, prev: Dot | undefined, place: DotPlace): Dot {
  const st = prev ?? { x: place.x, y: place.y, trail: [] };
  const rate = place.kind === 'link' ? 0.03 : 0.012;
  const k = f.motion === null ? 1 : Math.min(1, f.dt * rate);
  st.x += (place.x - st.x) * k;
  st.y += (place.y - st.y) * k;
  if (f.motion === null) st.trail = [];
  else {
    st.trail.push({ x: st.x, y: st.y });
    if (st.trail.length > 10) st.trail.shift();
  }
  return st;
}

function paintDots(ctx: Paint2D, f: TopoFrame, places: readonly Placed[], s: TopoState): void {
  const { l, p, motion, now } = f;
  const next = new Map<string, Dot>();
  for (const { item, place } of places) {
    const st = stepDot(f, s.dots.get(item.id), place);
    next.set(item.id, st);
    if (place.kind === 'machine') continue;
    paintDot(ctx, f, { at: st, place, col: toneColor(p, item.tone), fade: 1, tag: item.tag });
  }
  for (const g of motion?.gone ?? []) {
    const st = s.dots.get(g.item.id);
    const fade = Math.max(0, 1 - (now - g.at) / MOTION_MS.topoFade);
    if (st === undefined || fade <= 0) continue;
    next.set(g.item.id, st);
    const place: DotPlace = { ...l.prod, kind: 'node' };
    paintDot(ctx, f, { at: st, place, col: toneColor(p, g.item.tone), fade, tag: g.item.tag });
  }
  s.dots = next;
}

/** One topology's painter, holding its eased dots, node flashes and sparks between frames. */
export function createTopoPainter(): TopoPainter {
  const s: TopoState = { dots: new Map(), sparks: [], seen: new Set(), flash: { gh: 0, prod: 0 } };
  return {
    paint(ctx, f) {
      const { l, p, model, motion, now } = f;
      ctx.clearRect(0, 0, l.w, l.h);
      ctx.fillStyle = p.grid;
      for (let x = 12; x < l.w; x += 18) {
        for (let y = 12; y < l.h; y += 18) ctx.fillRect(x, y, 1, 1);
      }
      const spin = motion === null ? 0 : now * 0.0005;
      const places = model.items.map((item) => ({ item, place: dotPlace(l, item, spin) }));
      noteEffects(f, s);
      paintLinks(ctx, f, litLinks(places));
      paintIntake(ctx, f);
      model.machines.forEach((m, i) => {
        const at = l.ms[i];
        if (at !== undefined) paintHex(ctx, f, m, at);
      });
      paintNodes(ctx, f, s.flash);
      paintDots(ctx, f, places, s);
      paintSparks(ctx, p.green, s.sparks);
    },
  };
}
